import * as React from 'react';

import {
  enumCurrencyCode,
  enumPaymentMethod,
  enumPaymentTerm,
  PaymentMethod,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import {
  ContentSection,
  DatePicker,
  Grid,
  NumberField,
  TextArea,
  TextField,
  type DateValue,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftBillInfo,
  $effectiveBillInfo,
  DraftBillInfo,
} from '../../_state/billDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';
import { BillStatusBadge } from '../_shared/BillStatusBadge';

import { BillDetailFooter } from './BillDetailFooter';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { anyCalendarDateToCalendarDate } from '~/shared/utils/dateTime';
import { toMoney } from '~/shared/utils/money';

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ BILL TAB INFO ⚛️ ⚛️ ⚛️
//
export const BillTabInfo: React.FC<Props> = ({ disabled = false }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const effectiveBill = useAtomValue($effectiveBillInfo);
  const setDraftBillInfo = useSetAtom($currentDraftBillInfo);

  // Callbacks
  const handleFieldChange = React.useCallback(
    (updates: Partial<DraftBillInfo>) => {
      if (!effectiveBill) return;

      setDraftBillInfo({
        documentDate: effectiveBill.documentDate,
        documentNumber: effectiveBill.documentNumber,
        dueDate: effectiveBill.dueDate,
        eligibleAt: effectiveBill.eligibleAt,
        paymentTerm: effectiveBill.paymentTerm,
        preferredPaymentMethod: effectiveBill.preferredPaymentMethod,
        notes: effectiveBill.notes,
        amount: effectiveBill.amount,
        facilityId: effectiveBill.facilityId,
        ...updates,
      });
    },
    [effectiveBill, setDraftBillInfo],
  );

  if (!effectiveBill) {
    return (
      <div className="p-4 text-neutral/60">{t.bills.detail.empty.noData}</div>
    );
  }

  return (
    <FinanceDetailLayout
      className="@BillTabInfo h-auto"
      footer={<BillDetailFooter />}
    >
      <ContentSection
        title={t.bills.detail.sections.basicInfo}
        titleClassName="text-lg font-semibold text-neutral/90"
      >
        <Grid gap4 cols4 className="items-start">
          <TextField
            label={t.bills.detail.fields.type}
            value={t.enums.billType[effectiveBill.type] ?? effectiveBill.type}
            isDisabled
          />
          <TextField
            label={t.bills.detail.fields.status}
            leftAdornment={<BillStatusBadge status={effectiveBill.status} />}
            isDisabled
          />
          <SelectFacility
            label={t.bills.detail.fields.facility}
            value={effectiveBill.facilityId ?? undefined}
            onChange={(value) =>
              handleFieldChange({ facilityId: value ?? null })
            }
            isDisabled={disabled}
          />
          <TextField
            label={t.bills.detail.fields.documentNumber}
            value={effectiveBill.documentNumber ?? ''}
            onChange={(value) =>
              handleFieldChange({ documentNumber: value || null })
            }
            isDisabled={disabled}
          />
          <DatePicker
            label={t.bills.detail.fields.documentDate}
            value={effectiveBill.documentDate as DateValue | undefined}
            onChange={(value) =>
              handleFieldChange({
                documentDate: anyCalendarDateToCalendarDate(value ?? undefined),
              })
            }
            isDisabled={disabled}
          />
          <DatePicker
            label={t.bills.detail.fields.dueDate}
            value={effectiveBill.dueDate as DateValue | undefined}
            onChange={(value) =>
              handleFieldChange({
                dueDate: anyCalendarDateToCalendarDate(value ?? undefined),
              })
            }
            isDisabled={disabled}
          />
          <SelectEnum<PaymentTerm>
            label={t.bills.detail.fields.paymentTerm}
            enumValue={enumPaymentTerm}
            value={effectiveBill.paymentTerm ?? undefined}
            onChange={(value) =>
              handleFieldChange({ paymentTerm: value ?? null })
            }
            isDisabled={disabled}
          />
          <DatePicker
            label={t.bills.detail.fields.eligibleAt}
            value={effectiveBill.eligibleAt as DateValue | undefined}
            onChange={(value) =>
              handleFieldChange({
                eligibleAt: anyCalendarDateToCalendarDate(value ?? undefined),
              })
            }
            isDisabled={disabled}
          />
          <SelectEnum<PaymentMethod>
            label={t.bills.detail.fields.preferredPaymentMethod}
            enumValue={enumPaymentMethod}
            value={effectiveBill.preferredPaymentMethod ?? undefined}
            onChange={(value) =>
              handleFieldChange({ preferredPaymentMethod: value ?? null })
            }
            isDisabled={disabled}
          />
          <NumberField
            label={t.bills.detail.fields.amount}
            value={effectiveBill.amount?.amount ?? undefined}
            onChange={(value) =>
              handleFieldChange({
                amount:
                  value != null
                    ? toMoney({
                        amount: value,
                        currencyCode:
                          effectiveBill.amount?.currencyCode ??
                          enumCurrencyCode.USD,
                      })
                    : null,
              })
            }
            formatOptions={{
              style: 'currency',
              currency:
                effectiveBill.amount?.currencyCode ?? enumCurrencyCode.USD,
            }}
            isDisabled={disabled}
          />
          <TextField
            label={t.bills.detail.fields.currency}
            value={effectiveBill.amount?.currencyCode ?? enumCurrencyCode.USD}
            isDisabled
          />

          <div className="col-span-4">
            <TextArea
              label={t.bills.detail.fields.notes}
              value={effectiveBill.notes ?? ''}
              onChange={(value) => handleFieldChange({ notes: value || null })}
              rows={4}
              isDisabled={disabled}
            />
          </div>
        </Grid>
      </ContentSection>
    </FinanceDetailLayout>
  );
};
